/**
 * Read-only menu-item detail modal for the school order table. Opens when an
 * admin clicks an item row. Follows the Figma "Product details" pattern — a
 * tabbed layout with the tab row's underline doubling as the header separator —
 * but trimmed for read-only use: no in-modal ordering, narrower, smaller image.
 * Quantity entry stays in the order table.
 *
 *   - General:   dietary/allergen tags, description, ingredients, allergen
 *                disclaimer, with the meal image alongside.
 *   - Nutrition: the per-serving facts table only (no image). For a composed
 *                meal it is the whole meal's total, with a note naming any
 *                component the total leaves out (PROD-1554). The modal is
 *                customer-facing (parents open it from the day view), so a
 *                missing value reads "Not available", never internal wording.
 */

import {
  formatNutritionFactValue,
  NUTRITION_FACT_ROWS,
  type NutritionFactKey,
  type NutritionFactUnit,
} from '@ordo/constants/nutrition'
import { cn } from '@ordo/ui/cn'
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '@ordo/ui/dialog'
import { Skeleton } from '@ordo/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@ordo/ui/tabs'
import { AllergenTagChip, DietaryTagChip } from '@ordo/ui/tag-chip'
import { Utensils } from 'lucide-react'
import { type ReactElement, useEffect, useState } from 'react'

import { type MenuItemDetail, useMenuItemDetailQuery } from '@/api/menu-item-detail'
import { AssetImage } from '@/components/asset-image'
import { condimentListLabel } from '@/lib/school-orders/breakdown'

// Underline tab styling — the tab list's bottom border is the modal's main
// separator, so it spans the full width beneath the title.
const TABS_LIST_CLASS = 'h-auto w-full justify-start gap-1 rounded-none border-b border-border bg-transparent p-0'
const TABS_TRIGGER_CLASS =
  'relative -mb-px rounded-none border-b-2 border-transparent bg-transparent px-4 pb-2.5 pt-1 text-muted-foreground shadow-none data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none'

// Facts rendered as indented sub-rows under their parent macro.
const INDENTED_FACTS = new Set<NutritionFactKey>([
  'saturatedFatG',
  'transFatG',
  'dietaryFiberG',
  'totalSugarsG',
  'addedSugarsG',
])

/** Customer-facing copy for a nutrient (or serving size) with no data. */
export const NUTRITION_NOT_AVAILABLE = 'Not available'

/** Pure: a nutrient's cell text — the shared formatter, minus its internal "Needs data". */
export function nutritionValueLabel(value: number | null, unit: NutritionFactUnit): string {
  return value === null ? NUTRITION_NOT_AVAILABLE : formatNutritionFactValue(value, unit)
}

/**
 * Pure: the note under a meal's nutrition table naming what its total leaves
 * out ("Totals don't include: Ketchup, Milk."), or null when nothing is.
 */
export function nutritionExcludesNote(excludes: readonly string[] | null | undefined): string | null {
  const names = (excludes ?? []).map((name) => name.trim()).filter((name) => name !== '')
  return names.length > 0 ? `Totals don't include: ${names.join(', ')}.` : null
}

// Ordo's standard allergen-handling disclaimer.
const ALLERGEN_DISCLAIMER =
  'Prepared in a kitchen that handles meat, seafood, wheat, dairy, soy, eggs, nuts, and sesame.'

/** Title-case a component kind enum (e.g. MAIN_ENTREE → "Main Entree"). */
function kindLabel(kind: string | null): string | null {
  if (!kind) {
    return null
  }
  return kind
    .toLowerCase()
    .split('_')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ')
}

export interface MenuItemDetailModalProps {
  /** MenuItem id to show; `null` keeps the modal closed. */
  menuItemId: string | null
  /**
   * The service day (`YYYY-MM-DD`) the row was clicked on. The detail then
   * describes the item AS SERVED that day — a dated substitution swaps it for
   * the days the rule covers (PROD-1386) — so the modal never contradicts
   * the row that opened it.
   */
  serviceDate?: string | null
  /**
   * The item's formatted per-unit price (from the order-table row data).
   * Rendered in the header when present — the school-order grid keeps
   * prices off the planning page, so click-in is where admins check one.
   */
  priceLabel?: string | null
  /**
   * Which condiments ship with each meal (from the order-table row data,
   * like `priceLabel` — the detail endpoint doesn't carry them). Rendered
   * as a read-only General-tab section so a row showing "Condiments: …"
   * never opens a modal that omits them (PROD-136).
   */
  condiments?: Array<{ name: string; quantity: number }> | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function MenuItemDetailModal({
  menuItemId,
  serviceDate,
  priceLabel,
  condiments,
  open,
  onOpenChange,
}: MenuItemDetailModalProps): ReactElement {
  const { data: item, isPending, error } = useMenuItemDetailQuery(open ? menuItemId : null, serviceDate)
  const loading = open && Boolean(menuItemId) && isPending
  const hasNutrition = Boolean(item?.nutrition)

  // Reset to the General tab whenever a different item opens.
  const [tab, setTab] = useState('general')
  useEffect(() => {
    if (open) {
      setTab('general')
    }
  }, [open, menuItemId])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl sm:p-0">
        <DialogHeader className="px-4 pt-4 sm:px-6 sm:pt-6">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 pr-8">
            <DialogTitle>{item?.title ?? (loading ? 'Loading…' : 'Item details')}</DialogTitle>
            {item && kindLabel(item.componentKind) ? (
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                {kindLabel(item.componentKind)}
              </span>
            ) : null}
            {item && priceLabel ? <span className="text-sm font-medium tabular-nums">{priceLabel}</span> : null}
          </div>
        </DialogHeader>

        <DialogBody className="pt-3 sm:mx-0 sm:pt-4">
          {loading ? (
            <DetailSkeleton />
          ) : error ? (
            <p className="text-sm text-destructive">Couldn't load item details. {error.message}</p>
          ) : item ? (
            <Tabs value={hasNutrition ? tab : 'general'} onValueChange={setTab}>
              <TabsList className={TABS_LIST_CLASS}>
                <TabsTrigger value="general" className={TABS_TRIGGER_CLASS}>
                  General
                </TabsTrigger>
                {hasNutrition ? (
                  <TabsTrigger value="nutrition" className={TABS_TRIGGER_CLASS}>
                    Nutrition
                  </TabsTrigger>
                ) : null}
              </TabsList>

              <TabsContent value="general" className="mt-5">
                <div className="flex flex-col gap-5 sm:flex-row sm:gap-6">
                  <div className="order-2 min-w-0 flex-1 space-y-4 sm:order-1">
                    <GeneralTab item={item} condiments={condiments} />
                  </div>
                  <ItemImage imageUri={item.imageUri} />
                </div>
              </TabsContent>

              {hasNutrition ? (
                <TabsContent value="nutrition" className="mt-5">
                  <NutritionTable item={item} />
                </TabsContent>
              ) : null}
            </Tabs>
          ) : null}
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}

function ItemImage({ imageUri }: { imageUri: string | null }): ReactElement {
  return (
    <div className="order-1 shrink-0 sm:order-2 sm:w-56">
      <div className="aspect-square w-full overflow-hidden rounded-lg bg-muted">
        <AssetImage
          src={imageUri}
          className="h-full w-full object-cover"
          fallback={
            <div className="flex h-full w-full items-center justify-center text-muted-foreground">
              <Utensils className="h-12 w-12" aria-hidden />
            </div>
          }
        />
      </div>
    </div>
  )
}

function GeneralTab({
  item,
  condiments,
}: {
  item: MenuItemDetail
  condiments?: Array<{ name: string; quantity: number }> | null
}): ReactElement {
  return (
    <>
      {item.dietaryTags.length > 0 || item.allergens.length > 0 ? (
        <div className="flex flex-wrap gap-1.5" aria-label="Dietary and allergen tags">
          {item.dietaryTags.map((tag) => (
            <DietaryTagChip key={tag} label={tag} />
          ))}
          {item.allergens.map((a) => (
            <AllergenTagChip key={a.id}>Contains {a.name}</AllergenTagChip>
          ))}
        </div>
      ) : null}

      {item.description ? <p className="text-sm leading-relaxed">{item.description}</p> : null}

      {item.ingredients ? (
        <section>
          <h3 className="mb-1.5 text-sm font-semibold">Ingredients</h3>
          <p className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">{item.ingredients}</p>
        </section>
      ) : null}

      <CondimentsSection condiments={condiments} />

      <p className="rounded-lg bg-muted/50 p-3 text-xs leading-relaxed text-muted-foreground">{ALLERGEN_DISCLAIMER}</p>
    </>
  )
}

/**
 * Read-only "Condiments" General-tab section (PROD-136) — mirrors the
 * Ingredients section's anatomy. Exported so tests can SSR-render it
 * directly: the Radix Dialog portals its content, which emits nothing
 * under `renderToStaticMarkup`.
 */
export function CondimentsSection({
  condiments,
}: {
  condiments?: Array<{ name: string; quantity: number }> | null
}): ReactElement | null {
  if (!condiments || condiments.length === 0) {
    return null
  }
  return (
    <section>
      <h3 className="mb-1.5 text-sm font-semibold">Condiments</h3>
      <p className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">{condimentListLabel(condiments)}</p>
    </section>
  )
}

/**
 * The Nutrition tab's facts table, plus the "Totals don't include" note for a
 * meal whose total leaves components out. Exported so tests can SSR-render it
 * directly: the Radix Dialog portals its content, which emits nothing under
 * `renderToStaticMarkup`.
 */
export function NutritionTable({
  item,
}: {
  item: Pick<MenuItemDetail, 'nutrition' | 'servingSizeLabel' | 'nutritionExcludes'>
}): ReactElement | null {
  const { nutrition } = item
  if (!nutrition) {
    return null
  }
  const excludesNote = nutritionExcludesNote(item.nutritionExcludes)
  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-lg border">
        <NutritionRow label="Serving size" value={item.servingSizeLabel ?? NUTRITION_NOT_AVAILABLE} striped={false} />
        {NUTRITION_FACT_ROWS.map((row, i) => (
          <NutritionRow
            key={row.key}
            label={row.label}
            value={nutritionValueLabel(nutrition[row.key], row.unit)}
            indent={INDENTED_FACTS.has(row.key)}
            striped={i % 2 === 0}
          />
        ))}
      </div>
      {excludesNote ? <p className="text-xs leading-relaxed text-muted-foreground">{excludesNote}</p> : null}
    </div>
  )
}

function NutritionRow({
  label,
  value,
  indent = false,
  striped,
}: {
  label: string
  value: string
  indent?: boolean
  striped: boolean
}): ReactElement {
  return (
    <div className={cn('flex items-center justify-between gap-2 px-3 py-2 text-sm', striped && 'bg-muted/40')}>
      <span className={cn(indent ? 'pl-4 text-muted-foreground' : 'font-medium text-foreground')}>{label}</span>
      <span className="tabular-nums text-foreground">{value}</span>
    </div>
  )
}

function DetailSkeleton(): ReactElement {
  return (
    <div className="space-y-4">
      <Skeleton className="h-9 w-48" />
      <div className="flex flex-col gap-5 sm:flex-row sm:gap-6">
        <div className="order-2 min-w-0 flex-1 space-y-3 sm:order-1">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
        <Skeleton className="order-1 aspect-square w-full shrink-0 rounded-lg sm:order-2 sm:w-56" />
      </div>
    </div>
  )
}
